import { createRouter, createWebHistory } from 'vue-router'
import { SEOManager } from '@/utils/seoUtils.js'

const routes = [{
  path: '/',
  name: 'TuiJian',
  component: () => import('@/views/TuiJian.vue')
}, {
  path: '/allapp',
  name: 'AllApp',
  component: () => import('@/views/AllApp.vue')
},
{
  path: '/appdetails/:id',
  name: 'AppDetails',
  component: () => import('@/views/AppDetails.vue')
},
{
  path: '/fenlei',
  name: 'Fenlei',
  component: () => import('@/views/AppFenlei.vue')
},
{
  path: '/search',
  name: 'Search',
  component: () => import('@/views/AppSearch.vue')
},
{
  path: '/article',
  name: 'Article',
  component: () => import('@/views/ArticleList.vue')
},
{
  path: '/article/:id',
  name: 'ArticleDetails',
  component: () => import('@/views/ArticleDetails.vue')
},
{
  path: '/article/edit/:id?',
  name: 'ArticleEdit',
  component: () => import('@/views/ArticleEdit.vue'),
  meta: { requiresAuth: true }
},
{
  path: '/fankui',
  name: 'FanKui',
  component: () => import('@/views/FanKui.vue')
},
{
  path: '/ranks',
  name: 'Ranks',
  component: () => import('@/views/RanksList.vue')
},

{
  path: '/login',
  name: 'Login',
  component: () => import('@/views/Login.vue')
},
{
  path: '/register',
  name: 'Register',
  component: () => import('@/views/Register.vue')
},
{
  path: '/myinfo',
  name: 'MyInfo',
  component: () => import('@/views/MyInfo.vue'),
  meta: { requiresAuth: true }
},
{
  path: '/shop',
  name: 'Shop',
  component: () => import('@/views/Shop.vue'),
  meta: { requiresAuth: true }
},
{
  path: '/account',
  name: 'AccountSettings',
  component: () => import('@/views/AccountSettings.vue'),
  meta: { requiresAuth: true }
},
{
  path: '/friends',
  redirect: '/myinfo'
},
{
  path: '/chat',
  name: 'Chat',
  component: () => import('@/views/Chat.vue'),
  meta: { requiresAuth: true }
},

{
  path: '/channel',
  name: 'Channel',
  component: () => import('@/views/Channel.vue'),
  meta: { requiresAuth: true }
},
{
  path: '/user/:userId',
  name: 'UserProfile',
  component: () => import('@/views/UserProfile.vue'),
  meta: { requiresAuth: true }
},
{
  path: '/rules',
  name: 'Rules',
  component: () => import('@/views/RulesPage.vue')
},
{
  path: '/mianze',
  name: 'MianZe',
  component: () => import('@/views/MianZe.vue')
},
{
  path: '/auth/qq/callback',
  name: 'QQCallback',
  component: () => import('@/views/QQCallback.vue')
},
{
  path: '/wxlogin',
  name: 'WxLoginRedirect',
  component: () => import('@/views/WxLoginRedirect.vue')
},
{
  path: '/:pathMatch(.*)*',
  name: 'NotFound',
  component: () => import('@/views/ErrorPage.vue')
}]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// 路由守卫
router.beforeEach((to, from, next) => {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true'
  const userInfo = localStorage.getItem('userInfo')
  const isDev = process.env.NODE_ENV !== 'production'

  // 检查是否需要登录
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // 开发环境下放宽ArticleEdit页面的登录限制，便于本地验证
    if (isDev && to.name === 'ArticleEdit') {
      next()
      return
    }
    if (!isLoggedIn || !userInfo) {
      // 未登录：保存目标路径并跳转到首页 + 显示登录弹窗
      try {
        localStorage.setItem('postLoginRedirect', to.fullPath)
      } catch (e) {
        console.warn('postLoginRedirect set failed', e)
      }
      next({
        path: '/',
        query: { login: '1' }
      })
    } else {
      next()
    }
  } else if ((to.name === 'Login' || to.name === 'Register') && isLoggedIn && userInfo) {
    // 已登录用户访问登录或注册页面，重定向到首页
    next('/')
  } else {
    next()
  }
})

// 路由后置守卫 - 统一设置页面标题
router.afterEach((to) => {
  // 切换路由后自动滚动到顶部 (带平滑动画)
  // 尝试滚动 window 和 .framework (主容器)
  try {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (e) {
    window.scrollTo(0, 0)
  }

  const framework = document.querySelector('.framework')
  if (framework) {
    try {
      framework.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e) {
      framework.scrollTop = 0
    }
  }

  // 根据路由名称设置页面标题
  const routeNameToConfigMap = {
    'TuiJian': 'home',
    'AllApp': 'allapp',
    'AppDetails': 'appdetails',
    'Fenlei': 'fenlei',
    'Search': 'search',
    'Article': 'article',
    'ArticleDetails': 'articledetails',
    'ArticleEdit': 'articleedit',
    'FanKui': 'fankui',
    // 【2026-10-30 全面覆盖】原先缺失映射的路由：跳转后标题停留上一页
    'Ranks': 'ranks',
    'Channel': 'channel',
    'Chat': 'chat',
    'UserProfile': 'userprofile',
    'QQCallback': 'qqcallback',
    'WxLoginRedirect': 'wxlogin',
    'Login': 'login',
    'Register': 'register',
    'MyInfo': 'myinfo',
    'AccountSettings': 'myinfo',
    'Friends': 'myinfo',
    'Shop': 'shop',
    'Rules': 'rules',
    'MianZe': 'mianze',
    'NotFound': 'error'
  }

  const configKey = routeNameToConfigMap[to.name]
  if (configKey) {
    // 提取路由参数用于动态标题
    const params = {
      id: to.params.id,
      userId: to.params.userId,
      userName: to.query.name || to.query.userName || '',
      appName: to.query.appName || to.params.appName,
      articleTitle: to.query.articleTitle || to.params.articleTitle
    }

    const seoConfig = SEOManager.getPageConfig(configKey, params)
    SEOManager.setPageMeta(seoConfig)
  }
})

// 处理 Chunk 加载失败（单页应用更新后旧文件名失效的问题）
router.onError((error) => {
  const pattern = /Loading chunk (\d+) failed|Failed to fetch dynamically imported module/;
  const isChunkLoadFailed = pattern.test(error.message);
  if (isChunkLoadFailed) {
    window.location.reload();
  }
})

export default router